import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { PageHeader, LoadingTable, ErrorState } from '../components/States';
import { DataTable } from '../components/DataTable';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import * as api from '../lib/api';
import { Plus, Building2, ListTree } from 'lucide-react';

const NATURES = [{ v: 'costo', l: 'Costo' }, { v: 'ricavo', l: 'Ricavo' }, { v: 'patrimoniale', l: 'Patrimoniale' }];

export default function AnalisiCGAnagrafiche() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'control'; // sezione Analisi CG aperta al Controllo di gestione
  const [companies, setCompanies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filterCompany, setFilterCompany] = useState('all');

  const [compDlg, setCompDlg] = useState(null); // null | {}(new) | row(edit)
  const [accDlg, setAccDlg] = useState(null);
  const [confirm, setConfirm] = useState(null); // {kind,row}

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [c, a] = await Promise.all([api.cgaCompanies(), api.cgaAccounts()]);
      setCompanies(c); setAccounts(a);
    } catch (e) { setErr(api.apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const compName = (id) => companies.find((c) => c.id === id)?.code || '—';
  const filteredAccounts = filterCompany === 'all' ? accounts
    : filterCompany === 'common' ? accounts.filter((a) => !a.company_id)
    : accounts.filter((a) => a.company_id === filterCompany || !a.company_id);

  async function saveCompany(form) {
    try {
      if (compDlg?.id) await api.cgaCompanyUpdate(compDlg.id, form);
      else await api.cgaCompanyCreate(form);
      toast.success('Società salvata'); setCompDlg(null); load();
    } catch (e) { toast.error(api.apiError(e)); }
  }
  async function saveAccount(form) {
    try {
      const payload = { ...form, company_id: form.company_id === 'common' ? null : form.company_id };
      if (accDlg?.id) await api.cgaAccountUpdate(accDlg.id, payload);
      else await api.cgaAccountCreate(payload);
      toast.success('Conto salvato'); setAccDlg(null); load();
    } catch (e) { toast.error(api.apiError(e)); }
  }
  async function doDelete() {
    try {
      if (confirm.kind === 'company') await api.cgaCompanyDelete(confirm.row.id);
      else await api.cgaAccountDelete(confirm.row.id);
      toast.success('Eliminato'); setConfirm(null); load();
    } catch (e) { toast.error(api.apiError(e)); setConfirm(null); }
  }

  if (loading) return <div><PageHeader title="Anagrafiche" description="Società del gruppo e piano dei conti" /><LoadingTable /></div>;
  if (err) return <div><PageHeader title="Anagrafiche" /><ErrorState message={err} onRetry={load} /></div>;

  return (
    <div data-testid="cga-anagrafiche-page">
      <PageHeader title="Anagrafiche" description="Società del gruppo e piano dei conti (mastrini)" />
      {!isAdmin && <div className="mb-4 text-xs text-muted-foreground rounded-lg border bg-muted/40 px-3 py-2">Le anagrafiche sono in sola lettura per il tuo ruolo.</div>}
      <Tabs defaultValue="companies">
        <TabsList>
          <TabsTrigger value="companies" data-testid="tab-companies"><Building2 className="h-4 w-4 mr-1.5" />Società</TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts"><ListTree className="h-4 w-4 mr-1.5" />Piano dei conti</TabsTrigger>
        </TabsList>

        <TabsContent value="companies" className="mt-4">
          {isAdmin && <div className="mb-3 flex justify-end"><Button size="sm" onClick={() => setCompDlg({})} data-testid="add-company-btn"><Plus className="h-4 w-4 mr-1.5" />Nuova società</Button></div>}
          <DataTable
            rows={companies}
            columns={[
              { key: 'code', header: 'Codice', render: (r) => <span className="font-semibold">{r.code}</span> },
              { key: 'name', header: 'Ragione sociale' },
              { key: 'currency', header: 'Valuta' },
              { key: 'is_intercompany_parent', header: 'Capogruppo', render: (r) => r.is_intercompany_parent ? <Badge variant="secondary">Sì</Badge> : '—' },
              { key: 'active', header: 'Stato', render: (r) => r.active ? <Badge variant="secondary">Attiva</Badge> : <Badge variant="outline">Disattiva</Badge> },
            ]}
            actions={{ canWrite: isAdmin, onEdit: (r) => setCompDlg(r), onDelete: (r) => setConfirm({ kind: 'company', row: r }) }}
            emptyText="Nessuna società. Crea la prima società del gruppo."
          />
        </TabsContent>

        <TabsContent value="accounts" className="mt-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="w-64">
              <Select value={filterCompany} onValueChange={setFilterCompany}>
                <SelectTrigger data-testid="accounts-company-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i conti</SelectItem>
                  <SelectItem value="common">Solo comuni</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && <Button size="sm" onClick={() => setAccDlg({})} data-testid="add-account-btn"><Plus className="h-4 w-4 mr-1.5" />Nuovo conto</Button>}
          </div>
          <DataTable
            rows={filteredAccounts}
            columns={[
              { key: 'code', header: 'Codice', render: (r) => <span className="font-semibold">{r.code}</span> },
              { key: 'description', header: 'Descrizione' },
              { key: 'nature', header: 'Natura', render: (r) => <Badge variant="outline">{r.nature}</Badge> },
              { key: 'company_id', header: 'Ambito', render: (r) => r.company_id ? compName(r.company_id) : <Badge variant="secondary">Comune</Badge> },
            ]}
            actions={{ canWrite: isAdmin, onEdit: (r) => setAccDlg(r), onDelete: (r) => setConfirm({ kind: 'account', row: r }) }}
            emptyText="Nessun conto. Aggiungi il piano dei conti."
          />
        </TabsContent>
      </Tabs>

      {compDlg && <CompanyDialog data={compDlg} onClose={() => setCompDlg(null)} onSave={saveCompany} />}
      {accDlg && <AccountDialog data={accDlg} companies={companies} onClose={() => setAccDlg(null)} onSave={saveAccount} />}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi eliminazione?</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.row?.code} — {confirm?.row?.name || confirm?.row?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-modal-cancel-button">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-modal-confirm-button">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CompanyDialog({ data, onClose, onSave }) {
  const [f, setF] = useState({ code: data.code || '', name: data.name || '', currency: data.currency || 'EUR', active: data.active ?? true, is_intercompany_parent: data.is_intercompany_parent ?? false });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="form-modal">
        <DialogHeader><DialogTitle>{data.id ? 'Modifica società' : 'Nuova società'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Codice</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="company-code-input" /></div>
          <div><Label>Ragione sociale</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="company-name-input" /></div>
          <div><Label>Valuta</Label><Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} /></div>
          <div className="flex items-center justify-between"><Label>Capogruppo (consolidamento)</Label><Switch checked={f.is_intercompany_parent} onCheckedChange={(v) => setF({ ...f, is_intercompany_parent: v })} /></div>
          <div className="flex items-center justify-between"><Label>Attiva</Label><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => onSave(f)} data-testid="company-save-btn">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccountDialog({ data, companies, onClose, onSave }) {
  const [f, setF] = useState({ code: data.code || '', description: data.description || '', nature: data.nature || 'costo', company_id: data.company_id || 'common' });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="form-modal">
        <DialogHeader><DialogTitle>{data.id ? 'Modifica conto' : 'Nuovo conto'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Codice conto</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="account-code-input" /></div>
          <div><Label>Descrizione</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="account-desc-input" /></div>
          <div><Label>Natura</Label>
            <Select value={f.nature} onValueChange={(v) => setF({ ...f, nature: v })}>
              <SelectTrigger data-testid="account-nature-select"><SelectValue /></SelectTrigger>
              <SelectContent>{NATURES.map((n) => <SelectItem key={n.v} value={n.v}>{n.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Ambito</Label>
            <Select value={f.company_id} onValueChange={(v) => setF({ ...f, company_id: v })}>
              <SelectTrigger data-testid="account-company-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="common">Comune (tutte le società)</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={() => onSave(f)} data-testid="account-save-btn">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
